import { useEffect, useState } from "react";
import { Monitor, Tablet, Smartphone, ExternalLink, Eye, MousePointerClick, Search as SearchIcon, Store } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Link } from "react-router-dom";

const DEVICES = { desktop: { w: 1280, label: "Desktop", icon: Monitor }, tablet: { w: 768, label: "Tablet", icon: Tablet }, mobile: { w: 390, label: "Mobile", icon: Smartphone } };

export default function SellerStorefrontPreview() {
  const [slug, setSlug] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [stats, setStats] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/marketplace/storefront/me").then((r) => setSlug(r.data.storefront?.slug || "")).catch(() => setSlug("")).finally(() => setReady(true));
    api.get("/marketplace/seller/storefront/analytics").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const url = slug ? `${window.location.origin}/${slug}` : "";
  const dev = DEVICES[device];
  const maxViews = Math.max(1, ...((stats?.series || []).map((s) => s.views)));

  return (
    <div data-testid="seller-storefront-preview-page">
      <PageHeader title="Store Preview & Analytics" icon={Eye} subtitle="See exactly how your public store looks on every device, and track how customers engage with it." />

      {ready && !slug && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700" data-testid="no-store-yet">
          You haven't set up your store URL yet. Go to <Link to="/agent/storefront" className="font-semibold underline">Store Settings</Link> to claim your store and start selling.
        </div>
      )}

      {slug && (
        <>
          {stats && (
            <div className="mb-5 grid gap-3 sm:grid-cols-4" data-testid="store-analytics">
              <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="flex items-center gap-1.5 text-xs text-slate-500"><Eye className="h-3.5 w-3.5" /> Store views</p><p className="mt-1 text-2xl font-bold text-slate-900">{stats.views}</p></div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="flex items-center gap-1.5 text-xs text-slate-500"><MousePointerClick className="h-3.5 w-3.5" /> Product clicks</p><p className="mt-1 text-2xl font-bold text-slate-900">{stats.product_clicks}</p></div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="flex items-center gap-1.5 text-xs text-slate-500"><Store className="h-3.5 w-3.5" /> Category clicks</p><p className="mt-1 text-2xl font-bold text-slate-900">{stats.category_clicks}</p></div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="flex items-center gap-1.5 text-xs text-slate-500"><SearchIcon className="h-3.5 w-3.5" /> Searches</p><p className="mt-1 text-2xl font-bold text-slate-900">{stats.searches}</p></div>
            </div>
          )}

          {stats && stats.series?.length > 0 && (
            <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
              <p className="mb-3 text-xs font-semibold text-slate-500">Views · last {stats.series.length} day(s)</p>
              <div className="flex h-28 items-end gap-2 overflow-x-auto">
                {stats.series.map((s) => (
                  <div key={s.day} className="flex w-10 shrink-0 flex-col items-center justify-end gap-1">
                    <span className="text-[10px] font-semibold text-slate-500">{s.views}</span>
                    <div className="w-6 rounded-t bg-blue-500/80" style={{ height: `${Math.max(6, (s.views / maxViews) * 80)}px` }} title={`${s.day}: ${s.views}`} />
                    <span className="text-[9px] text-slate-400">{s.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats && stats.top_products?.length > 0 && (
            <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
              <p className="mb-2 text-xs font-semibold text-slate-500">Most-clicked products</p>
              {stats.top_products.map((p) => <div key={p.product_id} className="flex justify-between border-b border-slate-50 py-1.5 text-sm last:border-0"><span className="truncate text-slate-700">{p.name}</span><span className="font-semibold text-slate-500">{p.clicks} clicks</span></div>)}
            </div>
          )}

          {/* Device preview */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1 rounded-full bg-white p-1 ring-1 ring-slate-200" data-testid="device-toggle">
                {Object.entries(DEVICES).map(([k, d]) => {
                  const I = d.icon;
                  return <button key={k} onClick={() => setDevice(k)} data-testid={`device-${k}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${device === k ? "brand-gradient-bg text-white" : "text-slate-600"}`}><I className="h-3.5 w-3.5" /> {d.label}</button>;
                })}
              </div>
              <a href={url} target="_blank" rel="noreferrer noopener noreferrer" data-testid="open-live-store" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-blue-300"><ExternalLink className="h-3.5 w-3.5" /> Open live store</a>
            </div>
            <div className="flex justify-center overflow-x-auto">
              <div className="shrink-0 overflow-hidden rounded-xl border-4 border-slate-800 bg-white shadow-lg transition-all" style={{ width: dev.w }} data-testid="preview-frame-wrap">
                <iframe key={device} title="store-preview" src={url} className="block h-[640px] w-full border-0" data-testid="store-preview-iframe" />
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-slate-400">Live preview at {device} width ({dev.w}px). This shows your currently published store.</p>
          </div>
        </>
      )}
    </div>
  );
}
